import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading,
  setLocation,
  setError,
  updateConfig,
  setInitialized,
} from "../../store/GeolocationReducer/GeolocationReducer";

function getDivineTimezoneOffset() {
  const offsetMinutes = new Date().getTimezoneOffset();
  return -offsetMinutes / 60; // Convert to divine decimal format
}

/**
 * Optimized Geolocation Hook with Redux Store Integration
 * 
 * Features:
 * - Global state management with Redux
 * - Configurable options
 * - Caching mechanism to prevent unnecessary API calls
 * - Retry logic with exponential backoff
 * - Single initialization across the app
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.enableGeolocation - Enable browser geolocation API
 * @param {boolean} options.enableIPLookup - Enable IP-based location lookup
 * @param {number} options.cacheTimeout - Cache timeout in milliseconds (default: 24 hours)
 * @param {number} options.retryAttempts - Number of retry attempts (default: 3)
 * @param {number} options.retryDelay - Delay between retries in milliseconds (default: 1000)
 * @param {boolean} options.forceRefresh - Force refresh location data
 * @returns {Object} Location state and control functions
 */
export function useOptimizedGeolocation(options = {}) {
  const dispatch = useDispatch();
  const geolocationState = useSelector((state) => state.geolocation);
  
  const {
    enableGeolocation = true,
    enableIPLookup = true,
    cacheTimeout = 24 * 60 * 60 * 1000, // 24 hours
    retryAttempts = 3,
    retryDelay = 1000,
    forceRefresh = false,
  } = options;

  // Update config when options change
  useEffect(() => {
    dispatch(updateConfig({
      enableGeolocation,
      enableIPLookup,
      cacheTimeout,
      retryAttempts,
      retryDelay,
    }));
  }, [dispatch, enableGeolocation, enableIPLookup, cacheTimeout, retryAttempts, retryDelay]);

  const fetchLocationFromIP = useCallback(async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      return {
        city: data.city || "",
        region: data.region || "",
        country: data.country_name || "",
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        timezone: getDivineTimezoneOffset(),
      };
    } catch (error) {
      throw new Error(`IP lookup failed: ${error.message}`);
    }
  }, []);

  const fetchLocationFromGeolocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!("geolocation" in navigator)) {
        reject(new Error("Geolocation not supported"));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            
            // Get additional details from IP if enabled
            let locationData = {
              latitude,
              longitude,
              timezone: getDivineTimezoneOffset(),
            };

            if (enableIPLookup) {
              try {
                const ipData = await fetchLocationFromIP();
                locationData = {
                  ...locationData,
                  city: ipData.city,
                  region: ipData.region,
                  country: ipData.country,
                };
              } catch (ipError) {
                console.warn("IP lookup failed, using geolocation only:", ipError.message);
                // Continue with just geolocation data
              }
            }

            resolve(locationData);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          let errorMessage = "Permission denied for geolocation";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Permission denied for geolocation";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Location information unavailable";
              break;
            case error.TIMEOUT:
              errorMessage = "Location request timed out";
              break;
            default:
              errorMessage = "An unknown error occurred";
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes
        }
      );
    });
  }, [enableIPLookup, fetchLocationFromIP]);

  const fetchLocationWithRetry = useCallback(async (attempt = 1) => {
    try {
      dispatch(setLoading(true));
      
      let locationData;
      
      if (enableGeolocation) {
        try {
          locationData = await fetchLocationFromGeolocation();
        } catch (geolocationError) {
          console.warn("Geolocation failed, falling back to IP lookup:", geolocationError.message);
          if (enableIPLookup) {
            locationData = await fetchLocationFromIP();
          } else {
            throw geolocationError;
          }
        }
      } else if (enableIPLookup) {
        locationData = await fetchLocationFromIP();
      } else {
        throw new Error("Both geolocation and IP lookup are disabled");
      }

      dispatch(setLocation(locationData));
      return locationData;
    } catch (error) {
      if (attempt < retryAttempts) {
        console.warn(`Location fetch attempt ${attempt} failed, retrying...`, error.message);
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        return fetchLocationWithRetry(attempt + 1);
      } else {
        dispatch(setError(error.message));
        throw error;
      }
    }
  }, [
    dispatch,
    enableGeolocation,
    enableIPLookup,
    retryAttempts,
    retryDelay,
    fetchLocationFromGeolocation,
    fetchLocationFromIP,
  ]);

  const shouldFetchLocation = useCallback(() => {
    // Don't fetch if already loading
    if (geolocationState.loading) return false;
    
    // Force refresh if requested
    if (forceRefresh) return true;
    
    // Don't fetch if already initialized and within cache timeout
    if (geolocationState.isInitialized && geolocationState.lastFetched) {
      const timeSinceLastFetch = Date.now() - geolocationState.lastFetched;
      return timeSinceLastFetch > cacheTimeout;
    }
    
    // Fetch if not initialized
    return !geolocationState.isInitialized;
  }, [
    geolocationState.loading,
    geolocationState.isInitialized,
    geolocationState.lastFetched,
    forceRefresh,
    cacheTimeout,
  ]);

  // Initialize location on mount
  useEffect(() => {
    if (shouldFetchLocation()) {
      fetchLocationWithRetry().catch((error) => {
        console.error("Failed to fetch location:", error.message);
      });
    }
  }, [shouldFetchLocation, fetchLocationWithRetry]);

  // Control functions
  const refreshLocation = useCallback(() => {
    return fetchLocationWithRetry();
  }, [fetchLocationWithRetry]);

  const resetLocation = useCallback(() => {
    dispatch(setInitialized(false));
  }, [dispatch]);

  return {
    // Location data
    ...geolocationState,
    
    // Control functions
    refreshLocation,
    resetLocation,
    
    // Utility functions
    isLocationAvailable: geolocationState.isInitialized && !geolocationState.error,
    isLocationExpired: geolocationState.lastFetched 
      ? Date.now() - geolocationState.lastFetched > cacheTimeout 
      : false,
  };
}

export default useOptimizedGeolocation;

